import React, { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { ModalMask } from '../styles';
import FeedbackStartingModal from './FeedbackStartingModal';
import {
  FeedbackModalButton,
  FeedbackActivityModal,
  ActivityContainer,
  ActivityPicture,
  ActivityTextContainer,
  ActivityCompletedTitle,
  MoreDetailsLink,
  StarsContainer,
  DidNotCompleteCheckbox,
  FeedbackModalActivityTitle,
  PageNumber,
  FeedbackActivityModalTitle,
  CheckboxContainer,
  DetailsContainer,
  ImageNotAvaliable,
} from './styles';

import { CurrentWeekday } from '../../../util/Date';
import StarRating from './StarRating';
import { useLazyQuery, useMutation, useQuery } from 'react-apollo';

import { ClientContext } from '../../Client/ClientContext';
import RadioButtons from './RadioButtons';
import { SummaryMainSkill } from '../../Client/TaskRoute/ActivityTab/ActivityModal/styles';
import { ADD_FEEDBACK } from '../../../graphql/mutations/addFeedback';
import { CLIENT_QUERY } from '../../../graphql/queries/client';
import { AddFeedback, AddFeedbackVariables } from '../../../graphql/mutations/types/AddFeedback';
import { GET_FEEDBACK_FROM_CHILD_AND_WEEK } from '../../../graphql/queries/getFeedbackFromChildAndWeek';
import {
  GetFeedbackFromChildAndWeek,
  GetFeedbackFromChildAndWeekVariables,
} from '../../../graphql/queries/types/GetFeedbackFromChildAndWeek';
import { Client_client_children_assignedActivities } from '../../../graphql/queries/types/Client';
import { CHILD_CONSULTATION_NOTES } from '../../../graphql/queries/childConsultationNotes';
import {
  ChildConsultationNotes as ChildConsultationData,
  ChildConsultationNotesVariables as ChildConsultationVariables,
} from '../../../graphql/queries/types/ChildConsultationnotes';
import { CONSULTATION_QUERY } from '../../../graphql/queries/consultation';
import { Consultation, ConsultationVariables } from '../../../graphql/queries/types/Consultation';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { ICC_PRODUCT_TYPES, CPC_PRODUCT_TYPES } from '../../../util/Product';

const FeedbackModal = () => {
  const [openFeedbackModal, setOpenFeedbackModal] = useState(false);
  const [openStartingFeedbackModal, setOpenStartingFeedbackModal] = useState(true);
  const [openEndingFeedbackModal, setOpenEndingFeedbackModal] = useState(false);
  const [openContinueFeedbackModal, setOpenContinueFeedbackModal] = useState(false);
  //pagenumber: -1 is starting modal, 0 is all the activties, 1 is ending modal
  const [pageNumber, setPageNumber] = useState(-1);
  const [totalPages, setTotalPages] = useState(0);
  const [rating, setRating] = useState(0);
  const [didNotComplete, setDidNotComplete] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [nextButtonDisabled, setNextButtonDisabled] = useState(true);
  const [openReasonModal, setOpenReasonModal] = useState(false);
  const [ratingReason, setRatingReason] = useState('');
  const [ratingReasonRemarks, setRatingReasonRemarks] = useState('');
  const [reasonNextButtonDisabled, setReasonNextButtonDisabled] = useState(true);
  const [activityDetails, setActivityDetails] = useState<string[]>([]);
  const [missingActivities, setMissingActivities] = useState<Client_client_children_assignedActivities[]>([]);
  const [consultationId, setConsultationId] = useState('');
  const [childActivities, setChildActivities] = useState<Client_client_children_assignedActivities[]>([]);
  const [pastEndOfCourse, setPastEndOfCourse] = useState(false);
  const [feedbackData, setFeedbackData] = useState<GetFeedbackFromChildAndWeek>();
  const { t } = useTranslation();
  const { client } = useContext(ClientContext);

  const child = client.children.filter((child) => child.id == client.user.activeChildId)[0];
  const isIcc = ICC_PRODUCT_TYPES.includes(child.product.productTypesName);
  const childId = child.id;
  const isCPC = CPC_PRODUCT_TYPES.includes(child.product.productTypesName);
  const [childPreviousWeek, setChildPreviousWeek] = useState(
    pastEndOfCourse ? 12 : isIcc ? Math.ceil(child.currentWeek / 2) - 1 : child.currentWeek - 1,
  );
  const childPreviousConsultationIndexForArray = childPreviousWeek - 1;

  //Query for getting array of activities from feedback table
  const [getFeedback, { data: lazyQueryfeedbackData }] = useLazyQuery<
    GetFeedbackFromChildAndWeek,
    GetFeedbackFromChildAndWeekVariables
  >(GET_FEEDBACK_FROM_CHILD_AND_WEEK);

  useEffect(() => {
    setFeedbackData(lazyQueryfeedbackData);
  }, [lazyQueryfeedbackData]);

  useEffect(() => {
    getFeedback({ variables: { childId: parseInt(child.id), currentWeek: childPreviousWeek } });
  }, [child, childPreviousWeek]);

  useEffect(() => {
    setChildPreviousWeek(pastEndOfCourse ? 12 : isIcc ? Math.ceil(child.currentWeek / 2) - 1 : child.currentWeek - 1);
  }, [pastEndOfCourse]);

  const childConsultationInfo = useQuery<ChildConsultationData, ChildConsultationVariables>(CHILD_CONSULTATION_NOTES, {
    variables: { childId },
  });

  //All of the child's assigned activties
  const childAssignedActivities = child.assignedActivities;

  useEffect(() => {
    //2. Getting an array of consultation IDs from the consultation notes, filtering so only those auto-generated (part of the 12 weeks) are left
    const consultationArray = childConsultationInfo?.data?.child?.consultations;
    const filteredArray = consultationArray
      ?.filter((activity) => activity.notes?.mainComplaint === 'Auto Generated')
      .map((consultation) => consultation.id);

    //3. Sorting the strings in ascending order, result is an array of consultations (12 for 12 weeks of pea)
    const sortedArray = filteredArray?.sort((a, b) => Number(a) - Number(b));
    if (sortedArray) {
      try {
        setConsultationId(sortedArray[childPreviousConsultationIndexForArray]);
        if (child.currentWeek == 12 && filteredArray?.length != 12) {
          setConsultationId(sortedArray[sortedArray?.length - 1]);
        }
      } catch (e) {
        console.log('Consultation Id Error from feedback feature: ', e);
      }
      //4. Getting the consultation Id for the previous week and setting it
    }

    if (childConsultationInfo?.data?.child.consultations) {
      const filteredFullconsultationArray = childConsultationInfo?.data?.child.consultations.filter(
        (activity) => activity.notes?.mainComplaint === 'Auto Generated',
      );
      if (filteredFullconsultationArray[filteredFullconsultationArray.length - 1]) {
        const lastConsultationDateMoment = moment(
          filteredFullconsultationArray[filteredFullconsultationArray.length - 1].consultationTime,
          'YYYY-MM-DD',
        );
        const currentDate = moment();
        if (currentDate.isAfter(lastConsultationDateMoment)) {
          setPastEndOfCourse(true);
        } else {
          setPastEndOfCourse(false);
        }
      }
    }
  }, [childConsultationInfo]);

  const consultationActivityData = useQuery<Consultation, ConsultationVariables>(CONSULTATION_QUERY, {
    variables: { consultationId },
  });

  useEffect(() => {
    //5. Getting all the possible activities assigned by the consultations
    if (consultationActivityData?.data?.consultation?.child?.activityChildren) {
      const activityChildrenArray = consultationActivityData.data.consultation.child.activityChildren;
      //6. Filter out an array of activties based on the consultation id to get out the activities for last week
      const lastWeekActivitiesIds = activityChildrenArray
        .filter((item) => item.consultation.id === consultationId)
        .map((item) => item.activity.id);
      //7. Based on the array of activities, filter out all the assigned activities that match the last week activties and set to child activities which is the final array of previous week activties
      setChildActivities(childAssignedActivities.filter((activity) => lastWeekActivitiesIds?.includes(activity.id)));
    }
  }, [consultationActivityData, consultationId]);

  useEffect(() => {
    //8. Update the rest of the variables downstairs with the new previous week activities array
    if (childActivities.length > 0) {
      fetchData();
    }
  }, [childActivities]);

  //Update the missing activities array
  const fetchData = () => {
    const missingActivityIDs =
      feedbackData?.getFeedbackFromChildAndWeek.map((activity) => activity.activityId.toString()) ?? [];
    setMissingActivities(childActivities.filter((activity) => !missingActivityIDs?.includes(activity.id)));
    setTotalPages(missingActivities.length);
  };

  //Update total pages and missing activities whenever data is loaded in
  useEffect(() => {
    fetchData();
  }, [feedbackData, rating, didNotComplete]);

  useEffect(() => {
    //Controls if start or contine modal appears
    if (missingActivities.length != childActivities.length && pageNumber == -1) {
      setOpenStartingFeedbackModal(false);
      setOpenContinueFeedbackModal(true);
    } else if (missingActivities.length == childActivities.length && pageNumber == -1) {
      setOpenStartingFeedbackModal(true);
      setOpenContinueFeedbackModal(false);
    }

    //Controls when ending modal appears
    if (missingActivities.length == 0 && pageNumber == 0) {
      setPageNumber(1);
      setOpenEndingFeedbackModal(true);
    } else {
      setOpenEndingFeedbackModal(false);
    }
  }, [missingActivities.length]);

  //Checking if the modal should appear, if there are missing activities (missing activities are the difference in activities between feedback database and previous week activities)
  useEffect(() => {
    //Use this to check if issue arises: console.log('Missing activities: ', missingActivities);
    setTotalPages(missingActivities.length);
    if ((missingActivities.length == 0 && pageNumber != 1) || childPreviousWeek == 0 || !child.consentFormCompleted) {
      setOpenFeedbackModal(false);
    } else if (missingActivities.length > 0 || pageNumber == 1) {
      setOpenFeedbackModal(true);
    }
  }, [missingActivities, pageNumber]);

  //Mutation to update database
  const [addFeedback, {}] = useMutation<AddFeedback, AddFeedbackVariables>(ADD_FEEDBACK, {
    refetchQueries: [
      { query: CLIENT_QUERY, variables: { clientId: client.id } },
      {
        query: GET_FEEDBACK_FROM_CHILD_AND_WEEK,
        variables: { childId: parseInt(child.id), currentWeek: childPreviousWeek },
      },
    ],
  });

  //Function to handle submission of data to database
  const handleFeedbackSubmission = async () => {
    if (didNotComplete) {
      setRating(0);
    }
    await addFeedback({
      variables: {
        activityId: parseInt(missingActivities[pageNumber].id),
        rating: rating,
        activityCompleted: !didNotComplete,
        remarks: ratingReasonRemarks,
        childId: parseInt(child.id),
        currentWeek: childPreviousWeek,
        ratingReason: ratingReason,
      },
    });
    getFeedback({ variables: { childId: parseInt(child.id), currentWeek: childPreviousWeek } });
    fetchData();
  };

  //Function for determining if it is sunday (7 is sunday)
  const currentDay = CurrentWeekday();

  //Function to handle and show the details of the activity
  const handleShowDetails = () => {
    //Function to spilt up the text chunks from the details
    if (missingActivities[pageNumber] != null) {
      setActivityDetails([...missingActivities[pageNumber].steps.split('\n')]);
    }
    setShowDetails(!showDetails);
  };

  //Function to handle 'start' or 'continue' click on starting modal
  const handleStartingModalClick = () => {
    fetchData();
    setOpenStartingFeedbackModal(false);
    setOpenContinueFeedbackModal(false);
    setDidNotComplete(false);
    setPageNumber(0);
  };

  //Handles the last "thank you" modal click
  const handleEndingModalClick = () => {
    setOpenEndingFeedbackModal(false);
    setOpenFeedbackModal(false);
    setPageNumber(-1);
  };

  //Gets data from starRating component and sets the rating on this level
  const handleStarData = (starData) => {
    setRating(starData);
  };

  //Handle Toggling for did not complete check box
  const handleDidNotCompleteCheckBox = () => {
    setDidNotComplete(!didNotComplete);
  };

  //Reset for all states when going to the next activity
  const handleGoingNextActivity = () => {
    handleFeedbackSubmission();
    setDidNotComplete(false);
    setNextButtonDisabled(true);
    setRating(0);
    setOpenReasonModal(false);
    setReasonNextButtonDisabled(true);
    setRatingReason('');
  };

  //After ratings <4/ did not complete, open the reason modal
  const handleOpenReasonModal = () => {
    if (didNotComplete) {
      setRating(0);
    }
    setOpenReasonModal(true);
  };

  //Button disabling handlers
  useEffect(() => {
    if (rating >= 1 || didNotComplete) {
      setNextButtonDisabled(false);
    } else {
      setNextButtonDisabled(!didNotComplete);
    }
    if (ratingReason !== '') {
      setReasonNextButtonDisabled(false);
    } else if (rating >= 4) {
      setReasonNextButtonDisabled(false);
    } else {
      setReasonNextButtonDisabled(true);
    }
  }, [rating, didNotComplete, ratingReason]);

  //Rating reason handler data handler for state lifting from radiobuttons component
  const handleRatingReasonData = (ratingData) => {
    setRatingReason(ratingData.ratingReason);
    setRatingReasonRemarks(ratingData.ratingReasonRemarks);
  };

  return (
    <>
      {openFeedbackModal && !isCPC ? (
        <ModalMask open={true}>
          <FeedbackStartingModal
            display={openStartingFeedbackModal}
            onClick={handleStartingModalClick}
            title={t('feedback.startModalTitle')}
            text={t('feedback.startModalText')}
            buttonText={t('feedback.startModalButton')}
          ></FeedbackStartingModal>

          <FeedbackStartingModal
            display={openContinueFeedbackModal}
            onClick={handleStartingModalClick}
            title={t('feedback.continueModalTitle')}
            text={t('feedback.continueModalText')}
            buttonText={t('feedback.continueModalButton')}
          ></FeedbackStartingModal>

          {missingActivities.map((activity, index) => (
            <FeedbackActivityModal key={index} display={index == pageNumber ? true : false}>
              {!showDetails && !openReasonModal && (
                <>
                  <FeedbackActivityModalTitle>{t('feedback.activityModalTitle')}</FeedbackActivityModalTitle>
                  <ActivityContainer>
                    <ActivityPicture inputUrl={'' + activity.mediaUrl}>
                      {activity.mediaUrl ? null : (
                        <ImageNotAvaliable>{t('feedback.imageNotAvaliable')}</ImageNotAvaliable>
                      )}
                    </ActivityPicture>
                    <ActivityTextContainer>
                      <FeedbackModalActivityTitle>{activity.name}</FeedbackModalActivityTitle>
                      <ActivityCompletedTitle>
                        {t('feedback.activityModalCompletedPart1')}{' '}
                        <span style={{ fontWeight: 'bold', color: '#5abaae' }}>{activity.checkInCount}</span>
                        {activity.checkInCount == 1
                          ? t('feedback.activityModalCompletedPart2Single')
                          : t('feedback.activityModalCompletedPart2Pural')}
                        {t('feedback.activityModalCompletedPart3')}
                      </ActivityCompletedTitle>
                      <MoreDetailsLink onClick={handleShowDetails}>
                        {t('feedback.activityModalMoreDetails')}
                      </MoreDetailsLink>
                    </ActivityTextContainer>
                  </ActivityContainer>
                  <StarsContainer didNotComplete={didNotComplete}>
                    <StarRating didNotComplete={didNotComplete} onData={handleStarData}></StarRating>
                  </StarsContainer>
                  <CheckboxContainer>
                    <DidNotCompleteCheckbox type="checkbox" onChange={handleDidNotCompleteCheckBox} />
                    {t('feedback.activityModalDidNotComplete')}
                  </CheckboxContainer>

                  <FeedbackModalButton
                    onClick={handleOpenReasonModal}
                    marginTop={'40px'}
                    marginBottom={'0px'}
                    style={{ width: '150px' }}
                    disabled={nextButtonDisabled}
                  >
                    {t('feedback.activityModalButton')}
                  </FeedbackModalButton>

                  <PageNumber>
                    {t('feedback.activityRemaining1')}
                    {totalPages} {totalPages == 1 ? t('feedback.activityRemaining') : t('feedback.activitiesRemaining')}
                  </PageNumber>
                </>
              )}
              {showDetails && (
                <>
                  <ActivityContainer>
                    <ActivityPicture inputUrl={'' + activity.mediaUrl}>
                      {activity.mediaUrl ? null : (
                        <ImageNotAvaliable>{t('feedback.imageNotAvaliable')}</ImageNotAvaliable>
                      )}
                    </ActivityPicture>
                    <ActivityTextContainer style={{ width: '200px' }}>
                      <FeedbackModalActivityTitle style={{ fontSize: '2rem' }}>
                        {activity.name}
                      </FeedbackModalActivityTitle>
                      <SummaryMainSkill style={{ margin: '0', marginTop: '10px' }}>
                        {activity.mainSkill.name}
                      </SummaryMainSkill>
                    </ActivityTextContainer>
                  </ActivityContainer>
                  <DetailsContainer>
                    {activityDetails.map((item) => (
                      <p style={{ marginTop: '7px' }}>{item}</p>
                    ))}
                  </DetailsContainer>
                  <FeedbackModalButton
                    onClick={handleShowDetails}
                    marginTop={'20px'}
                    marginBottom={'0px'}
                    style={{ width: '150px' }}
                    disabled={false}
                  >
                    {t('feedback.reasonModalButton')}
                  </FeedbackModalButton>
                </>
              )}
              {openReasonModal && (
                <>
                  <FeedbackActivityModalTitle style={{ fontSize: '2.3rem', marginBottom: '1rem' }}>
                    {t('feedback.reasonModalTitle')}
                  </FeedbackActivityModalTitle>
                  <RadioButtons
                    didNotComplete={didNotComplete}
                    rating={rating}
                    onData={handleRatingReasonData}
                  ></RadioButtons>
                  <FeedbackModalButton
                    onClick={handleGoingNextActivity}
                    marginTop={'40px'}
                    marginBottom={'0px'}
                    style={{ width: '150px' }}
                    disabled={reasonNextButtonDisabled}
                  >
                    {t('feedback.activityModalButton')}
                  </FeedbackModalButton>
                </>
              )}
            </FeedbackActivityModal>
          ))}

          {openEndingFeedbackModal && (
            <FeedbackStartingModal
              display={true}
              onClick={handleEndingModalClick}
              title={t('feedback.endModalTitle')}
              text={t('feedback.endModalText')}
              buttonText={t('feedback.endModalButton')}
            ></FeedbackStartingModal>
          )}
        </ModalMask>
      ) : null}
    </>
  );
};

export default FeedbackModal;
